///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, Component, computed, HostListener, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Store } from '@ngrx/store';

import { EMPTY, finalize } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { isEmptyString, SuiLoadingDirective, SuiTranslatePipe } from '@siklu/sui-lib';

import { ButtonsDirectivesModule } from '../shared/directives/buttons-directives/buttons-directives.module';
import { LoaderIds, PreloaderService } from '../services/preloader.service';
import { selectEmailVerificationTokenLength } from '../shared/store/systemInfo/system-info.selectors';
import { EmailVerificationService } from './email-verification.service';
import { EmailVerificationInputComponent } from './email-verification-input.component';
import { EMAIL_VERIFICATION_CONSTANTS } from './email-verification.constants';
import { Principal } from '../shared/auth/principal.service';
import { UserDTO } from '../generated/shared-types.model';

export interface EmailVerificationMessage {
    message: string;
    params?: Record<string, string | number>;
}

@Component({
    selector: 'winde-email-verification-dialog',
    templateUrl: './email-verification-dialog.component.html',
    styleUrl: './email-verification-dialog.component.scss',
    imports: [
        ButtonsDirectivesModule,
        FormsModule,
        MatDialogModule,
        SuiTranslatePipe,
        SuiLoadingDirective,
        EmailVerificationInputComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EmailVerificationDialogComponent implements OnInit, OnDestroy {
    public static readonly DIALOG_CONFIG = EMAIL_VERIFICATION_CONSTANTS.DIALOG_CONFIG;

    private readonly principal = inject(Principal);
    private readonly systemInfoStore = inject(Store);
    private readonly emailVerificationService = inject(EmailVerificationService);
    private readonly dialogRef = inject(MatDialogRef<EmailVerificationDialogComponent>);

    protected readonly loaderKey = LoaderIds.EMAIL_VERIFICATION_ID;
    protected readonly preloaderService = inject(PreloaderService);

    // Broadcast channel for cross-tab communication
    private readonly broadcastChannel = new BroadcastChannel(EMAIL_VERIFICATION_CONSTANTS.BROADCAST_CHANNEL_NAME);

    protected readonly token = signal([]);
    protected readonly sentTokens = signal(false);
    protected readonly isValidating = signal(false);
    protected readonly isSendingEmail = signal(false);
    protected readonly isEmailVerified = signal(false);
    protected readonly isTemporaryBlocked = signal(false);

    protected readonly errorMessage = signal<EmailVerificationMessage | null>(null);
    protected readonly infoMessage = signal<EmailVerificationMessage | null>(null);
    protected readonly successMessage = signal<EmailVerificationMessage | null>(null);

    protected readonly canVerify = computed(
        () => this.token().length === this.tokenLength() && this.token().every((char: string) => !isEmptyString(char))
    );
    protected readonly tokenLength = toSignal(this.systemInfoStore.select(selectEmailVerificationTokenLength));

    protected readonly verifyButtonText = computed(() => {
        if (this.isValidating()) {
            return 'emailVerification.button.verifying';
        } else {
            return 'emailVerification.button.verify';
        }
    });

    protected readonly resendButtonText = computed(() => {
        if (this.isSendingEmail()) {
            return 'emailVerification.button.sending';
        } else if (this.sentTokens()) {
            return 'emailVerification.button.resend';
        } else {
            return 'emailVerification.button.send';
        }
    });

    @HostListener('document:visibilitychange', [])
    onVisibilityChange() {
        if (document.visibilityState === 'visible') {
            this.principal.identity(true).then((user: UserDTO) => {
                if (user && !user.emailVerificationRequired) {
                    this.isEmailVerified.set(true);
                    this.dialogRef.close();
                }
            });
        }
    }

    ngOnInit(): void {
        this.setEmptyToken();
        this.setupBroadcastChannelListener();

        const token = this.principal.getStoredEmailVerificationToken();
        if (token) {
            this.token.set([...token]);
            this.sentTokens.set(true);
            this.validateToken();
        }

        /*
         * Email generation is triggered only by explicit user action to avoid:
         * - Sending multiple emails when several browser tabs are open in the same session
         * - Resending emails on page refresh
         * - Sending emails without user consent
         *
         * The user must click the "Send Code" button to request a verification email
         * instead of triggering generateNewTokenAndSendToEmail() automatically.
         */
    }

    ngOnDestroy(): void {
        if (!this.isEmailVerified()) {
            this.principal.forceLogout();
        }
        this.broadcastChannel?.close();
    }

    generateNewTokenAndSendToEmail(): void {
        this.setEmptyToken();
        this.clearMessages();
        this.isSendingEmail.set(true);
        this.infoMessage.set({ message: 'emailVerification.info.generationInProgress' });

        this.preloaderService.show(this.loaderKey);
        this.emailVerificationService
            .generateTokenAndSendToEmail()
            .pipe(
                finalize(() => {
                    this.isSendingEmail.set(false);
                    this.preloaderService.hide(this.loaderKey);
                }),
                catchError((error) => this.handleSendTokenError(error))
            )
            .subscribe(() => this.handleSendTokenSuccess());
    }

    validateToken(): void {
        if (!this.canVerify()) {
            return;
        }

        this.clearMessages();
        this.isValidating.set(true);
        this.infoMessage.set({ message: 'emailVerification.info.validationInProgress' });

        this.preloaderService.show(this.loaderKey);
        this.emailVerificationService
            .validateToken(this.getTokenString())
            .pipe(
                finalize(() => {
                    this.isValidating.set(false);
                    this.preloaderService.hide(this.loaderKey);
                }),
                catchError(() => this.handleVerificationError())
            )
            .subscribe(() => this.handleVerificationSuccess());
    }

    private setEmptyToken(): void {
        this.token.set(new Array<string>(this.tokenLength()).fill(''));
    }

    private getTokenString(): string {
        return this.token().join('');
    }

    private setupBroadcastChannelListener(): void {
        this.broadcastChannel.onmessage = (event: MessageEvent) => {
            if (event.data === EMAIL_VERIFICATION_CONSTANTS.EMAIL_VERIFIED) {
                this.isEmailVerified.set(true);
                this.dialogRef.close();
            }
        };
    }

    private handleSendTokenSuccess(): void {
        this.clearMessages();
        this.sentTokens.set(true);
        this.successMessage.set({ message: 'emailVerification.success.emailSent' });
    }

    private handleSendTokenError(error: Error) {
        this.clearMessages();
        if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.TooManyRequests) {
            const { remainingSeconds } = error.error;
            const remainingMinutes = Math.floor(remainingSeconds / 60);
            const message = remainingMinutes > 1 ? 'emailVerification.error.tryAgainLater' : 'emailVerification.error.tryAgainInMinute';
            this.errorMessage.set({ message, params: { remainingMinutes } });
            this.isTemporaryBlocked.set(true);
        } else {
            this.errorMessage.set({ message: 'emailVerification.error.emailNotSent' });
        }

        return EMPTY;
    }

    private handleVerificationSuccess(): void {
        this.clearMessages();
        this.isEmailVerified.set(true);
        this.successMessage.set({ message: 'emailVerification.success.emailVerified' });
        this.broadcastChannel.postMessage(EMAIL_VERIFICATION_CONSTANTS.EMAIL_VERIFIED);
        setTimeout(() => {
            this.dialogRef.close();
        }, EMAIL_VERIFICATION_CONSTANTS.CLOSE_DELAY);
    }

    private handleVerificationError() {
        this.clearMessages();
        this.errorMessage.set({ message: 'emailVerification.error.tokenInvalidOrExpired' });
        return EMPTY;
    }

    private clearMessages(): void {
        this.infoMessage.set(null);
        this.errorMessage.set(null);
        this.successMessage.set(null);
    }
}
